import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useUI } from '../contexts/UIProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import { compressImage } from '../utils/imageOptimizer';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { openAttachment } from '../utils/fileUtils';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import PhotoIcon from './icons/PhotoIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import TrashIcon from './icons/TrashIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import YouTubeIcon from './icons/YouTubeIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import MapPinIcon from './icons/MapPinIcon';
import CloseIcon from './icons/CloseIcon';
import CheckIcon from './icons/CheckIcon';
import DocumentIcon from './icons/DocumentIcon';
import type { User, TravelPlan, RiderFile } from '../types';

const COMMON_INSTRUMENTS = ['Vocals', 'Guitar', 'Bass', 'Drums', 'Keyboard', 'Piano', 'Saxophone', 'Trumpet', 'Violin', 'DJ', 'Percussion'];
const COMMON_GENRES = ['Rock', 'Pop', 'Jazz', 'Blues', 'Electronic', 'Hip-Hop', 'Classical', 'Folk', 'Metal', 'R&B', 'Soul', 'Funk'];

const EditProfilePage: React.FC = () => {
    const { currentUser, handleUpdateProfile } = useAuth();
    const { handleNavigation } = useNavigation();
    const { showToast } = useUI();
    const { t } = useLanguage();
    
    const [formData, setFormData] = useState<Partial<User>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [isProcessingImage, setIsProcessingImage] = useState(false);
    const [isBrandingOpen, setIsBrandingOpen] = useState(true);
    
    const [instrumentInput, setInstrumentInput] = useState('');
    const [genreInput, setGenreInput] = useState('');
    const [displayLocation, setDisplayLocation] = useState('');
    
    const bannerInputRef = useRef<HTMLInputElement>(null);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const cityInputRef = useRef<HTMLInputElement>(null);
    const riderFileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (currentUser) {
            setFormData({ 
                ...currentUser,
                instruments: currentUser.instruments || [],
                genres: currentUser.genres || [],
                socialLinks: currentUser.socialLinks || {},
                embeddedMedia: currentUser.embeddedMedia || [],
                photos: currentUser.photos || [],
                travelPlans: currentUser.travelPlans || [],
                rider: currentUser.rider || '',
                riderFiles: currentUser.riderFiles || []
            });
            const fullLoc = [currentUser.city, currentUser.region, currentUser.country].filter(Boolean).join(', ');
            setDisplayLocation(fullLoc);
        }
    }, [currentUser]);

    useEffect(() => {
        const initMaps = async () => {
            const API_KEY = (window as any).GOOGLE_MAPS_API_KEY || "PASTE_YOUR_GOOGLE_MAPS_API_KEY_HERE";
            try {
                await loadGoogleMaps(API_KEY);
                if (!(window as any).google || !cityInputRef.current) return;
                const autocomplete = new (window as any).google.maps.places.Autocomplete(cityInputRef.current, { 
                    fields: ['address_components', 'formatted_address', 'name']
                });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place.address_components) {
                        let city = '';
                        let reg = '';
                        let cntry = '';
                        for (const component of place.address_components) {
                            const types = component.types;
                            if (types.includes('locality')) city = component.long_name;
                            if (types.includes('administrative_area_level_1')) reg = component.long_name;
                            if (types.includes('country')) cntry = component.long_name;
                        }
                        const newCity = city || place.name || '';
                        setFormData(prev => ({ ...prev, city: newCity, region: reg, country: cntry }));
                        setDisplayLocation([newCity, reg, cntry].filter(Boolean).join(', '));
                    }
                });
            } catch (err) {}
        };
        initMaps();
    }, []);

    const isFan = currentUser?.subscriptionTier === 'fan_basic';

    const handleSave = async () => {
        if (!formData.city) {
            alert("Please ensure your home city is verified via the dropdown.");
            return;
        }
        setIsSaving(true);
        try {
            await handleUpdateProfile(formData as User);
            handleNavigation('profile', { user: formData });
        } catch (error) {
            showToast("Save failed");
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'banner' | 'avatar' | 'photo') => {
        if (e.target.files && e.target.files[0]) {
            setIsProcessingImage(true);
            try {
                const optimizedUrl = await compressImage(e.target.files[0]);
                if (field === 'banner') {
                    setFormData(prev => ({ ...prev, bannerUrl: optimizedUrl }));
                } else if (field === 'avatar') {
                    setFormData(prev => ({ ...prev, avatarUrl: optimizedUrl }));
                } else {
                    setFormData(prev => ({ ...prev, photos: [...(prev.photos || []), { id: Date.now().toString(), url: optimizedUrl }] }));
                }
            } catch (err) {
                showToast("Optimization failed.");
            } finally {
                setIsProcessingImage(false);
            }
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                const fileUrl = reader.result as string;
                const newFile: RiderFile = {
                    id: Date.now().toString(),
                    name: file.name,
                    url: fileUrl
                };
                setFormData(prev => ({ ...prev, riderFiles: [...(prev.riderFiles || []), newFile] }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddTravelPlan = () => {
        const newPlan: TravelPlan = {
            id: Date.now().toString(),
            city: '',
            region: '',
            country: '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 86400000).toISOString().split('T')[0]
        };
        setFormData(prev => ({ ...prev, travelPlans: [...(prev.travelPlans || []), newPlan] }));
    };

    const updateTravelPlan = (id: string, field: keyof TravelPlan, value: string, region?: string, country?: string) => {
        setFormData(prev => ({
            ...prev,
            travelPlans: prev.travelPlans?.map(p => p.id === id ? { ...p, [field]: value, ...(region !== undefined ? { region } : {}), ...(country !== undefined ? { country } : {}) } : p)
        }));
    };

    const removeTravelPlan = (id: string) => {
        setFormData(prev => ({ ...prev, travelPlans: prev.travelPlans?.filter(p => p.id !== id) }));
    };

    const handleAddInstrument = (val: string) => {
        const tag = val.trim();
        if (tag && !formData.instruments?.includes(tag)) {
            setFormData(prev => ({ ...prev, instruments: [...(prev.instruments || []), tag] }));
        }
        setInstrumentInput('');
    };

    const handleAddGenre = (val: string) => {
        const tag = val.trim();
        if (tag && !formData.genres?.includes(tag)) {
            setFormData(prev => ({ ...prev, genres: [...(prev.genres || []), tag] }));
        }
        setGenreInput('');
    };

    const removeInstrument = (tag: string) => {
        setFormData(prev => ({ ...prev, instruments: prev.instruments?.filter(i => i !== tag) }));
    };

    const removeGenre = (tag: string) => {
        setFormData(prev => ({ ...prev, genres: prev.genres?.filter(g => g !== tag) }));
    };

    const isDirty = JSON.stringify(currentUser) !== JSON.stringify(formData);

    return (
        <div className="space-y-6 font-urbanist pb-20 max-w-4xl mx-auto">
            <button onClick={() => handleNavigation('profile', { user: currentUser })} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                <ArrowLeftIcon className="h-5 w-5" />
                <span>{t('BACK')}</span>
            </button>

            <div className="bg-brand-surface rounded-lg p-6 flex flex-col md:flex-row justify-between items-start md:items-center border border-gray-800 gap-4 shadow-sm">
                <div>
                    <h2 className="text-3xl font-black text-white tracking-tighter uppercase">{t('EDIT_PROFILE')}</h2>
                    <p className="text-brand-muted text-sm uppercase font-bold tracking-widest">Professional Identity Hub</p>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={!isDirty || isSaving || isProcessingImage}
                    className="w-full md:w-auto bg-brand-orange text-white font-black px-10 py-3 rounded-full hover:bg-orange-600 disabled:opacity-30 shadow-lg shadow-brand-orange/20 uppercase tracking-widest text-xs transition-all"
                >
                    {isSaving ? 'Saving...' : (isProcessingImage ? 'Optimizing...' : 'Save Profile')}
                </button>
            </div>

            <section className="bg-brand-surface rounded-lg border border-gray-800 overflow-hidden shadow-sm">
                <button 
                    onClick={() => setIsBrandingOpen(!isBrandingOpen)}
                    className="w-full p-4 flex justify-between items-center hover:bg-gray-800/40 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <PhotoIcon className="h-5 w-5 text-brand-orange" />
                        <h3 className="text-lg font-black text-white uppercase tracking-widest">Visual Branding</h3>
                    </div>
                    <ChevronDownIcon className={`h-5 w-5 text-brand-muted transition-transform duration-300 ${isBrandingOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isBrandingOpen && (
                    <div className="p-6 pt-0 border-t border-gray-800 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                            <div className="flex flex-col items-center">
                                <label className="block text-[10px] font-extrabold text-brand-muted mb-3 uppercase tracking-widest w-full">Avatar Image</label>
                                <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                                    <img src={formData.avatarUrl} className="h-32 w-32 rounded-full border-4 border-gray-700 group-hover:border-brand-orange transition-colors object-cover shadow-xl" alt="Avatar"/>
                                    <div className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <PhotoIcon className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                                <input type="file" ref={avatarInputRef} onChange={e => handleImageUpload(e, 'avatar')} accept="image/*" className="hidden"/>
                            </div>
                            <div>
                                <label className="block text-[10px] font-extrabold text-brand-muted mb-3 uppercase tracking-widest">Banner Backdrop</label>
                                <div 
                                    className="h-32 rounded-lg bg-gray-700 bg-cover bg-center mb-3 border border-gray-600 shadow-inner group relative cursor-pointer overflow-hidden" 
                                    style={{backgroundImage: `url(${formData.bannerUrl})`, backgroundSize: 'cover'}} 
                                    onClick={() => bannerInputRef.current?.click()}
                                >
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-[10px] font-bold uppercase tracking-widest text-white">Update Banner</div>
                                </div>
                                <input type="file" ref={bannerInputRef} onChange={e => handleImageUpload(e, 'banner')} accept="image/*" className="hidden"/>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <div className="space-y-6">
                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">{t('IDENTITY')}</h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">{t('FULL_NAME')}</label>
                                <input type="text" value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-gray-700 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange" />
                            </div>
                            {!isFan && (
                                <div>
                                    <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">{t('PROFESSIONAL_TITLE')}</label>
                                    <input type="text" value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full bg-gray-700 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange" />
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">{t('BASE_CITY')}</label>
                            <div className="relative">
                                <input 
                                    ref={cityInputRef} 
                                    type="text" 
                                    value={displayLocation} 
                                    onChange={(e) => setDisplayLocation(e.target.value)}
                                    placeholder="Search city..." 
                                    className="w-full bg-gray-700 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange pr-10" 
                                />
                                {formData.city && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-400" />}
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">{t('BIO')}</label>
                        <textarea value={formData.collaborations || ''} onChange={(e) => setFormData(p => ({...p, collaborations: e.target.value}))} className="w-full bg-gray-700 text-white rounded-md p-4 outline-none resize-none leading-relaxed font-bold border border-gray-600 focus:border-brand-orange" rows={5} placeholder={isFan ? "Introduce yourself..." : "Describe your professional journey..."}/>
                    </div>
                </section>

                {!isFan && (
                    <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                        <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                            <h3 className="text-lg font-black text-white uppercase tracking-widest">Technical Rider</h3>
                        </div>
                        <div>
                            <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">Tech Specs & Needs</label>
                            <textarea value={formData.rider || ''} onChange={(e) => setFormData(p => ({...p, rider: e.target.value}))} className="w-full bg-gray-700 text-white rounded-md p-4 outline-none resize-none leading-relaxed font-bold border border-gray-600 focus:border-brand-orange" rows={4} placeholder="Detail your backline, audio channels, or stage setup requirements..."/>
                        </div>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <label className="block text-[10px] font-extrabold text-brand-muted uppercase tracking-widest">Uploaded Schematics (PDF/Images)</label>
                                <button onClick={() => riderFileInputRef.current?.click()} className="text-[10px] text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline">
                                    <PlusCircleIcon className="h-4 w-4"/> {t('ADD_DESTINATION')}
                                </button>
                                <input type="file" ref={riderFileInputRef} onChange={handleFileUpload} accept=".pdf,image/*" className="hidden"/>
                            </div>
                            <ul className="space-y-2">
                                {(formData.riderFiles || []).map((file) => (
                                    <li key={file.id} className="flex items-center justify-between bg-gray-800/40 p-3 rounded-lg border border-gray-700">
                                        <button 
                                            onClick={() => openAttachment(file)}
                                            className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
                                        >
                                            <DocumentIcon className="h-5 w-5 text-brand-orange"/>
                                            <span className="text-sm font-bold text-white truncate max-w-[200px]">{file.name}</span>
                                        </button>
                                        <button onClick={() => setFormData(p => ({...p, riderFiles: p.riderFiles?.filter(f => f.id !== file.id)}))} className="text-red-400 hover:text-red-300 p-1">
                                            <TrashIcon className="h-5 w-5"/>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>
                )}

                {!isFan && (
                    <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                        <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                            <h3 className="text-lg font-black text-white uppercase tracking-widest">{t('TRAVEL_PLANS')}</h3>
                            <button onClick={handleAddTravelPlan} className="text-[10px] text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline">
                                <PlusCircleIcon className="h-4 w-4"/> {t('ADD_DESTINATION')}
                            </button>
                        </div>
                        <div className="space-y-4">
                            {formData.travelPlans?.map((plan) => (
                                <TravelPlanItem 
                                    key={plan.id} 
                                    plan={plan} 
                                    onUpdate={updateTravelPlan} 
                                    onRemove={removeTravelPlan} 
                                />
                            ))}
                        </div>
                    </section>
                )}

                {!isFan && (
                    <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                        <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">{t('PROFESSIONAL_SKILLS')}</h3>
                        <div className="p-4 bg-gray-800/40 rounded-xl border border-gray-700 space-y-6">
                            <div>
                                <label className="block text-[10px] font-extrabold text-brand-orange mb-2 uppercase tracking-widest">{t('CORE_INSTRUMENTS')}</label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.instruments?.map(i => (
                                        <span key={i} className="text-[10px] bg-brand-orange/10 text-brand-orange px-2 py-1 rounded border border-brand-orange/20 font-bold flex items-center gap-1 group">
                                            #{i}
                                            <button onClick={() => removeInstrument(i)} className="hover:text-white"><CloseIcon className="h-3 w-3" /></button>
                                        </span>
                                    ))}
                                </div>
                                <input 
                                    type="text" 
                                    list="instr-list"
                                    value={instrumentInput}
                                    onChange={(e) => setInstrumentInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddInstrument(instrumentInput); } }}
                                    placeholder="Add instrument..."
                                    className="w-full bg-gray-900/60 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange text-sm" 
                                />
                                <datalist id="instr-list">{COMMON_INSTRUMENTS.map(i => <option key={i} value={i} />)}</datalist>
                            </div>
                            <div>
                                <label className="block text-[10px] font-extrabold text-blue-400 mb-2 uppercase tracking-widest">{t('MAIN_GENRES')}</label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {formData.genres?.map(g => (
                                        <span key={g} className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-1 rounded border border-blue-400/20 font-bold flex items-center gap-1 group">
                                            #{g}
                                            <button onClick={() => removeGenre(g)} className="hover:text-white"><CloseIcon className="h-3 w-3" /></button>
                                        </span>
                                    ))}
                                </div>
                                <input 
                                    type="text" 
                                    list="genre-list"
                                    value={genreInput}
                                    onChange={(e) => setGenreInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddGenre(genreInput); } }}
                                    placeholder="Add genre..."
                                    className="w-full bg-gray-900/60 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange text-sm" 
                                />
                                <datalist id="genre-list">{COMMON_GENRES.map(g => <option key={g} value={g} />)}</datalist>
                            </div>
                        </div>
                    </section>
                )}

                {!isFan && (
                    <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                        <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">{t('SOCIAL_PRESENCE')}</h3>
                        <div className="space-y-4">
                            <SocialInput label="Facebook" icon={<FacebookIcon className="h-5 w-5 text-[#1877F2]" />} value={formData.socialLinks?.facebook || ''} onChange={val => setFormData({...formData, socialLinks: {...formData.socialLinks, facebook: val}})} placeholder="https://facebook.com/yourpage" />
                            <SocialInput label="Instagram" icon={<InstagramIcon className="h-5 w-5 text-[#E4405F]" />} value={formData.socialLinks?.instagram || ''} onChange={val => setFormData({...formData, socialLinks: {...formData.socialLinks, instagram: val}})} placeholder="https://instagram.com/yourpage" />
                            <SocialInput label="Youtube" icon={<YouTubeIcon className="h-5 w-5 text-red-600" />} value={formData.socialLinks?.youtube || ''} onChange={val => setFormData({...formData, socialLinks: {...formData.socialLinks, youtube: val}})} placeholder="https://youtube.com/@yourchannel" />
                        </div>
                    </section>
                )}

                {!isFan && (
                    <section className="bg-brand-surface rounded-lg p-6 space-y-4 border border-gray-800 shadow-sm">
                        <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">External Content Links</h3>
                        <p className="text-xs text-brand-muted italic">Links will open in a new window on the host site.</p>
                        <div className="space-y-4">
                            {(formData.embeddedMedia || []).map((item, idx) => (
                                <div key={item.id || idx} className="flex items-center gap-3">
                                    <input value={item.url} onChange={e => { const n = [...formData.embeddedMedia!]; n[idx] = {...item, url: e.target.value}; setFormData({...formData, embeddedMedia: n}); }} className="flex-1 bg-gray-700 p-3 rounded text-sm font-bold border border-gray-600 focus:border-brand-orange" placeholder="Link (YouTube, Spotify, SoundCloud)"/>
                                    <button onClick={() => setFormData({...formData, embeddedMedia: formData.embeddedMedia?.filter((_, i) => i !== idx)})} className="p-2 text-red-400 hover:bg-red-400/10 rounded-full"><TrashIcon className="h-5 w-5"/></button>
                                </div>
                            ))}
                            <button onClick={() => setFormData({...formData, embeddedMedia: [...(formData.embeddedMedia || []), { id: Date.now().toString(), type: 'unknown', url: '' }]})} className="text-xs text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline"><PlusCircleIcon className="h-4 w-4"/> Add External Link</button>
                        </div>
                    </section>
                )}

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                        <h3 className="text-lg font-black text-white uppercase tracking-widest">Portfolio Gallery</h3>
                        <button onClick={() => avatarInputRef.current?.click()} className="text-[10px] text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline">
                            <PlusCircleIcon className="h-4 w-4"/> Add Image
                        </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {(formData.photos || []).map((photo) => (
                            <div key={photo.id} className="relative group aspect-square">
                                <img src={photo.url} className="w-full h-full object-cover rounded-lg border border-gray-800" alt="Gallery"/>
                                <button onClick={() => setFormData(p => ({...p, photos: p.photos?.filter(ph => ph.id !== photo.id)}))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                    <TrashIcon className="h-4 w-4"/>
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

const SocialInput: React.FC<{ label: string, icon: React.ReactNode, value: string, onChange: (v: string) => void, placeholder: string }> = ({ label, icon, value, onChange, placeholder }) => (
    <div className="flex flex-col gap-2">
        <label className="text-[10px] font-extrabold text-brand-muted uppercase tracking-widest">{label}</label>
        <div className="flex items-center gap-3 bg-gray-700/40 rounded-md border border-gray-600 p-1 pr-3 focus-within:border-brand-orange transition-colors">
            <div className="bg-gray-800 p-2 rounded-md">{icon}</div>
            <input type="url" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="flex-1 bg-transparent text-white outline-none font-bold text-sm" />
        </div>
    </div>
);

const TravelPlanItem: React.FC<{ plan: TravelPlan, onUpdate: (id: string, field: keyof TravelPlan, value: string, region?: string, country?: string) => void, onRemove: (id: string) => void }> = ({ plan, onUpdate, onRemove }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [localValue, setLocalValue] = useState('');

    useEffect(() => {
        const full = [plan.city, plan.region, plan.country].filter(Boolean).join(', ');
        setLocalValue(full);
    }, [plan.city, plan.region, plan.country]);

    useEffect(() => {
        const initMaps = async () => {
            const API_KEY = (window as any).GOOGLE_MAPS_API_KEY || "PASTE_YOUR_GOOGLE_MAPS_API_KEY_HERE";
            try {
                await loadGoogleMaps(API_KEY);
                if (!(window as any).google || !inputRef.current) return;
                const autocomplete = new (window as any).google.maps.places.Autocomplete(inputRef.current, { 
                    fields: ['address_components', 'formatted_address', 'name']
                });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (place.address_components) {
                        let city = '';
                        let reg = '';
                        let cntry = '';
                        for (const component of place.address_components) {
                            const types = component.types;
                            if (types.includes('locality') || types.includes('sublocality')) city = component.long_name;
                            if (types.includes('administrative_area_level_1')) reg = component.long_name;
                            if (types.includes('country')) cntry = component.long_name;
                        }
                        const finalCity = city || place.name || '';
                        onUpdate(plan.id, 'city', finalCity, reg, cntry);
                        setLocalValue([finalCity, reg, cntry].filter(Boolean).join(', '));
                    }
                });
            } catch (err) {}
        };
        initMaps();
    }, [plan.id, onUpdate]);

    return (
        <div className="bg-gray-800/40 p-5 rounded-xl border border-gray-700 space-y-4 group transition-all hover:border-blue-500/30">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <MapPinIcon className="h-4 w-4 text-blue-400" />
                    <span className="text-[10px] font-black text-white uppercase tracking-widest">Travel Segment</span>
                </div>
                <button onClick={() => onRemove(plan.id)} className="text-red-400 hover:bg-red-400/10 p-2 rounded-full transition-colors opacity-0 group-hover:opacity-100">
                    <TrashIcon className="h-4 w-4" />
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <label className="block text-[9px] font-extrabold text-brand-muted mb-1 uppercase tracking-widest">Destination</label>
                    <div className="relative">
                        <input 
                            ref={inputRef} 
                            type="text" 
                            value={localValue} 
                            onChange={(e) => setLocalValue(e.target.value)}
                            placeholder="Search city..." 
                            className="w-full bg-gray-900/60 text-white rounded-lg p-3 outline-none border border-gray-600 font-bold focus:border-blue-500 pr-10" 
                        />
                        {plan.city && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-400" />}
                    </div>
                </div>
                <div>
                    <label className="block text-[9px] font-extrabold text-brand-muted mb-1 uppercase tracking-widest">From</label>
                    <input type="date" value={plan.startDate} onChange={e => onUpdate(plan.id, 'startDate', e.target.value)} className="w-full bg-gray-900/60 text-white rounded-lg p-3 outline-none border border-gray-600 font-bold font-mono focus:border-blue-500" />
                </div>
                <div>
                    <label className="block text-[9px] font-extrabold text-brand-muted mb-1 uppercase tracking-widest">To</label>
                    <input type="date" value={plan.endDate} onChange={e => onUpdate(plan.id, 'endDate', e.target.value)} className="w-full bg-gray-900/60 text-white rounded-lg p-3 outline-none border border-gray-600 font-bold font-mono focus:border-blue-500" />
                </div>
            </div>
        </div>
    );
};

export default EditProfilePage;